import { useState, useEffect, useCallback } from 'react';
import { itemService } from '../services/itemService';

/**
 * Custom hook for managing items
 */
export const useItems = (categoryId = null, page = 0, limit = 50, search = null) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const skip = page * limit;
      const result = await itemService.getAll(categoryId, skip, limit, search);
      setItems(result.data);
      setTotal(result.total);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch items');
    } finally {
      setLoading(false);
    }
  }, [categoryId, page, limit, search]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const createItem = async (itemData) => {
    setLoading(true);
    setError(null);
    try {
      const newItem = await itemService.create(itemData);
      setItems(prev => [...prev, newItem]);
      return newItem;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create item');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateItem = async (itemId, itemData) => {
    setLoading(true);
    setError(null);
    try {
      const updated = await itemService.update(itemId, itemData);
      setItems(prev => prev.map(item => item.id === itemId ? updated : item));
      return updated;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update item');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (itemId) => {
    setLoading(true);
    setError(null);
    try {
      await itemService.delete(itemId);
      setItems(prev => prev.filter(item => item.id !== itemId));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete item');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    items,
    total,
    loading,
    error,
    fetchItems,
    createItem,
    updateItem,
    deleteItem
  };
};
